import React, { useEffect, useState } from 'react'
import {
    Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, FormControl, FormControlLabel,
    InputLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography
} from '@mui/material'
import api from '../services/api'

const vazio = {
    username: { on: false, valor: '' },
    password: { on: false, valor: '' },
    enable_password: { on: false, valor: '' },
    port: { on: false, valor: '' },
    type_id: { on: false, valor: '' },
    interface: { on: false, valor: '' },
    status: { on: false, valor: '1' },
}

/**
 * Alteração em massa de hosts, no modelo "mass update" do Zabbix: cada campo
 * tem um checkbox que o habilita, e só o que estiver marcado é enviado.
 *
 * O motivo de não ser um formulário comum: num formulário, campo vazio é
 * ambíguo — "não mexer" e "apagar" viram a mesma coisa. Com opt-in explícito
 * não existe como sobrescrever a senha de 200 hosts sem ter pedido.
 *
 * O escopo "todos" NÃO manda lista de id nenhuma: o servidor monta a lista a
 * partir da empresa (do token, no cliente). Mandar 500 ids no corpo só pra
 * dizer "todos" seria dar ao navegador uma decisão que é do servidor.
 */
export default function BulkHostDialog({
    open, onClose, admin = false, hostIds = [], empresa = null, onDone
}) {
    const [campos, setCampos] = useState(vazio)
    const [escopo, setEscopo] = useState('selected')
    const [salvando, setSalvando] = useState(false)
    const [types, setTypes] = useState([])
    const [tuneis, setTuneis] = useState([])

    useEffect(() => {
        if (!open) return
        setCampos(vazio)
        setEscopo('selected')
        // Catálogo COMPLETO, não o `em_uso=1` do filtro. Trocar de template é
        // justamente o caso em que o modelo de destino ainda não está em uso —
        // oferecer só o que a empresa já tem impediria corrigir um host
        // cadastrado no template errado.
        api.get(admin ? '/admin/hermes/host/type/all' : '/hermes/host/type/all')
            .then((res) => setTypes(res.data || []))
            .catch(() => setTypes([]))

        // Interface é gravada pelo NOME do túnel, e a lista é por empresa. No
        // admin depende da empresa selecionada; sem ela não há o que oferecer.
        const rotaTunel = admin
            ? (empresa ? `admin/tunnel/all/${empresa}/` : null)
            : '/hermes/host/tunnel/all'
        if (!rotaTunel) { setTuneis([]); return }
        api.get(rotaTunel)
            .then((res) => setTuneis(res.data || []))
            .catch(() => setTuneis([]))
    }, [open, admin, empresa])

    const set = (nome, patch) =>
        setCampos((prev) => ({ ...prev, [nome]: { ...prev[nome], ...patch } }))

    const marcados = Object.entries(campos).filter(([, c]) => c.on)
    const nada = marcados.length === 0
    const alvo = escopo === 'all'
        ? 'todos os hosts da empresa'
        : `${hostIds.length} host(s) selecionado(s)`

    // Tudo num pedido só. Trocar template e porta é o par mais comum (SSH pra
    // TELNET muda os dois) e o servidor grava num UPDATE único — em chamadas
    // separadas, falhar na segunda deixaria template novo com porta velha.
    const montarCorpo = () => {
        const corpo = { action: 'update', escopo }
        if (escopo === 'selected') corpo.host_ids = hostIds
        if (admin && empresa) corpo.empresa = empresa
        if (campos.username.on) corpo.username = campos.username.valor
        if (campos.password.on) corpo.password = campos.password.valor
        if (campos.enable_password.on) corpo.enable_password = campos.enable_password.valor
        if (campos.port.on) corpo.port = campos.port.valor
        if (campos.type_id.on) corpo.type_id = campos.type_id.valor
        if (campos.interface.on) corpo.interface = campos.interface.valor
        if (campos.status.on) corpo.status = campos.status.valor
        return corpo
    }

    const rotulos = {
        username: 'usuário', password: 'senha', enable_password: 'enable password',
        port: 'porta', type_id: 'tipo/modelo', interface: 'interface', status: 'status',
    }

    const aplicar = async () => {
        const nomes = marcados.map(([k]) => rotulos[k]).join(', ')
        const desativando = campos.status.on && campos.status.valor === '0'
        if ((escopo === 'all' || desativando) &&
            !confirm(`Alterar ${nomes} em ${alvo}?`)) {
            return
        }

        setSalvando(true)
        const url = admin ? '/admin/hermes/host/bulk-action' : '/hermes/host/bulk-action'
        try {
            const res = await api.post(url, montarCorpo())
            setSalvando(false)
            if (res.data?.error) {
                // Erro de validação não grava nada: o servidor confere todos os
                // campos antes do UPDATE. Então o diálogo fica aberto pra
                // pessoa corrigir, em vez de fechar como se tivesse aplicado.
                onDone({ severity: 'error', message: res.data.error })
                return
            }
            onClose()
            const fora = res.data?.ignorados || 0
            onDone({
                severity: 'success',
                message: `${res.data?.count || 0} host(s) alterado(s) (${nomes})`
                    + (fora > 0 ? ` — ${fora} fora do escopo` : '')
            })
        } catch (e) {
            setSalvando(false)
            onDone({ severity: 'error', message: 'Falha ao aplicar a alteração' })
        }
    }

    const linha = (nome, rotulo, conteudo) => (
        <Box display="flex" alignItems="center" gap={1.5}>
            <Checkbox
                size="small"
                checked={campos[nome].on}
                onChange={(e) => set(nome, { on: e.target.checked })}
                inputProps={{ 'aria-label': `Alterar ${rotulo}` }}
            />
            <Box flex={1} sx={{ opacity: campos[nome].on ? 1 : 0.45 }}>{conteudo}</Box>
        </Box>
    )

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            // O tema global declara mode 'light' mas background.paper '#2B2E35'
            // (a barra lateral depende desse escuro). Sem forçar aqui, o
            // diálogo sai com papel escuro e texto de tema claro.
            PaperProps={{ sx: { bgcolor: 'background.default', color: 'text.primary' } }}>
            <DialogTitle>Alteração em massa</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Marque só o que deve mudar. O que ficar desmarcado permanece como está.
                </Typography>

                <FormControl sx={{ mb: 1 }}>
                    <RadioGroup row value={escopo} onChange={(e) => setEscopo(e.target.value)}>
                        <FormControlLabel
                            value="selected" control={<Radio size="small" />}
                            disabled={hostIds.length === 0}
                            label={<Typography variant="body2">{hostIds.length} selecionado(s)</Typography>}
                        />
                        <FormControlLabel
                            value="all" control={<Radio size="small" />}
                            label={<Typography variant="body2">Todos os hosts da empresa</Typography>}
                        />
                    </RadioGroup>
                </FormControl>

                {escopo === 'all' && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        A ação vale para todos os hosts da empresa, inclusive os que não estão
                        nesta página e os que o filtro atual esconde.
                    </Alert>
                )}

                <Divider sx={{ mb: 2 }} />

                <Box display="flex" flexDirection="column" gap={1.5}>
                    {linha('username', 'usuário',
                        <TextField fullWidth size="small" label="Usuário"
                            disabled={!campos.username.on}
                            value={campos.username.valor}
                            onChange={(e) => set('username', { valor: e.target.value })} />
                    )}
                    {linha('password', 'senha',
                        <TextField fullWidth size="small" label="Senha" type="password"
                            disabled={!campos.password.on}
                            value={campos.password.valor}
                            onChange={(e) => set('password', { valor: e.target.value })} />
                    )}
                    {linha('enable_password', 'enable password',
                        <TextField fullWidth size="small" label="Enable password" type="password"
                            disabled={!campos.enable_password.on}
                            value={campos.enable_password.valor}
                            onChange={(e) => set('enable_password', { valor: e.target.value })} />
                    )}
                    {linha('port', 'porta',
                        <TextField fullWidth size="small" label="Porta" type="number"
                            disabled={!campos.port.on}
                            value={campos.port.valor}
                            onChange={(e) => set('port', { valor: e.target.value })} />
                    )}
                    {linha('type_id', 'tipo e modelo',
                        <FormControl fullWidth size="small" disabled={!campos.type_id.on}>
                            <InputLabel>Tipo/Modelo</InputLabel>
                            <Select label="Tipo/Modelo" value={campos.type_id.valor}
                                onChange={(e) => set('type_id', { valor: e.target.value })}>
                                {types.map((t) => (
                                    <MenuItem key={t.id} value={t.id}>
                                        {t.vendor} - {t.type} ({t.protocol})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {linha('interface', 'interface',
                        <FormControl fullWidth size="small"
                            disabled={!campos.interface.on || tuneis.length === 0}>
                            <InputLabel>Interface</InputLabel>
                            <Select label="Interface" value={campos.interface.valor}
                                onChange={(e) => set('interface', { valor: e.target.value })}>
                                {tuneis.map((t) => (
                                    <MenuItem key={t.name} value={t.name}>{t.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {linha('status', 'status',
                        <FormControl fullWidth size="small" disabled={!campos.status.on}>
                            <InputLabel>Status do host</InputLabel>
                            <Select label="Status do host" value={campos.status.valor}
                                onChange={(e) => set('status', { valor: e.target.value })}>
                                <MenuItem value="1">Ativo</MenuItem>
                                <MenuItem value="0">Inativo</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                </Box>

                {campos.type_id.on && !campos.port.on && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        O tipo define como o backup é coletado, e o protocolo costuma vir com
                        porta própria (SSH 22, Telnet 23). Se o modelo novo usa outro
                        protocolo, marque a porta também.
                    </Alert>
                )}
                {campos.interface.on && tuneis.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {admin && !empresa
                            ? 'Selecione uma empresa na barra de filtros para listar as interfaces.'
                            : 'Nenhuma interface cadastrada para esta empresa.'}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} color="inherit">Cancelar</Button>
                <Button
                    variant="contained"
                    onClick={aplicar}
                    disabled={nada || salvando || (escopo === 'selected' && hostIds.length === 0)}
                    startIcon={salvando ? <CircularProgress size={16} /> : null}
                >
                    Aplicar
                </Button>
            </DialogActions>
        </Dialog>
    )
}
